import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAuthSession, waitForTier } from '../util/amplifyAuth';
import UserHeader from '../components/UserHeader/UserHeader';
import ProfileForm from '../components/ProfileForm/ProfileForm';
import FocusSettings from '../components/FocusSettings/FocusSettings';
import AssessmentSettings from '../components/AssessmentSettings/AssessmentSettings';
import LabConfigSettings from '../components/LabConfigSettings/LabConfigSettings';
import { NotificationSettings } from '../notifications';
import { getUserProfile, saveUserProfile, getSubscriptionStatus, createBillingPortalSession, changePlan, previewPlanChange, cancelPlanChange, cancelSubscription, resumeSubscription, PLAN_NAMES, SWITCHABLE_PLANS, PLAN_RANKS } from '../components/API';
import { getCachedProfile, setCachedProfile } from '../util/CacheUtil';
import { tierFromGroups } from '../util/featureConfig';
import { useToast } from '../components/Toast/ToastProvider';
import '../styles/Settings.css';

const patientSections = [
    { id: 'profile', label: 'Profile', desc: 'Name, birthdate, and personal info' },
    { id: 'focuses', label: 'Health Focuses', desc: 'Focus areas and assessments' },
    { id: 'sharing', label: 'Sharing & Access', desc: 'Manage doctor and friend connections' },
    { id: 'notifications', label: 'Notifications', desc: 'Reminders and push notifications' },
    { id: 'advanced', label: 'Advanced', desc: 'Lab defaults and ideal ranges' },
    { id: 'subscription', label: 'Subscription', desc: 'Manage your plan and billing' },
];

function Settings() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const cachedProfile = getCachedProfile();
    const [focuses, setFocuses] = useState(() => {
        return cachedProfile ? (cachedProfile.focuses || []) : null;
    });
    const [tier, setTier] = useState(0);
    const [sex, setSex] = useState(() => cachedProfile?.sex || null);
    const [isDoctor, setIsDoctor] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    // Deep-link support: /settings?section=subscription opens that tab directly
    const [searchParams] = useSearchParams();
    const requestedSection = searchParams.get('section');
    const validRequested = patientSections.some(s => s.id === requestedSection) ? requestedSection : null;
    const [activeSection, setActiveSection] = useState(validRequested || 'profile');
    const [mobileShowContent, setMobileShowContent] = useState(!!validRequested);

    const [profileData, setProfileData] = useState(cachedProfile);
    const [profileLoading, setProfileLoading] = useState(!cachedProfile);
    const [profileSubmitting, setProfileSubmitting] = useState(false);

    const [subscription, setSubscription] = useState(null);
    const [subLoading, setSubLoading] = useState(true);
    const [subError, setSubError] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [changeLoading, setChangeLoading] = useState(false);
    // Which plan option is mid-request — only that button shows "Working..." (others
    // are disabled to prevent double-submit but keep their normal label).
    const [loadingPlanId, setLoadingPlanId] = useState(null);
    // Confirmation modal: { plan, preview } — preview comes from preview-plan-change
    // (exact prorated cost today + current period end) before anything is charged.
    const [confirmChange, setConfirmChange] = useState(null);
    // Cancel-subscription confirmation modal
    const [confirmCancel, setConfirmCancel] = useState(false);

    useEffect(() => {
        fetchAuthSession()
            .then(session => {
                const groups = session.tokens?.idToken?.payload['cognito:groups'] || [];
                setTier(tierFromGroups(groups));
                setIsDoctor(groups.includes('Doctors'));
            })
            .catch(() => {})
            .finally(() => setAuthChecked(true));
    }, []);

    useEffect(() => {
        if (isDoctor) return;
        if (cachedProfile) {
            setProfileData(cachedProfile);
            if (focuses === null) setFocuses(cachedProfile.focuses || []);
            if (sex === null) setSex(cachedProfile.sex || null);
            setProfileLoading(false);
            return;
        }
        setProfileLoading(true);
        getUserProfile()
            .then(data => {
                setCachedProfile(data);
                setProfileData(data);
                if (focuses === null) setFocuses(data.focuses || []);
                if (sex === null) setSex(data.sex || null);
            })
            .catch(() => { if (focuses === null) setFocuses([]); })
            .finally(() => setProfileLoading(false));
    }, [isDoctor]);

    const loadSubscription = useCallback(() => {
        setSubLoading(true);
        setSubError(false);
        return getSubscriptionStatus()
            .then(res => { setSubscription(res?.subscription || null); })
            .catch(() => setSubError(true))
            .finally(() => setSubLoading(false));
    }, []);

    useEffect(() => {
        if (isDoctor || !authChecked) return;
        loadSubscription();
    }, [isDoctor, authChecked, loadSubscription]);

    const handlePortal = async () => {
        setPortalLoading(true);
        try {
            const res = await createBillingPortalSession();
            window.location.href = res.portalUrl;
        } catch {
            showToast('Unable to open subscription portal', 'error');
            setPortalLoading(false);
        }
    };

    const handleSelectPlan = async (plan) => {
        setLoadingPlanId(plan.priceId);
        setChangeLoading(true);
        try {
            const preview = await previewPlanChange(plan.priceId);
            setConfirmChange({ plan, preview });
        } catch (err) {
            showToast(err?.data?.error || 'Unable to preview plan change', 'error');
        } finally {
            setChangeLoading(false);
            setLoadingPlanId(null);
        }
    };

    const handleConfirmChange = async () => {
        if (!confirmChange) return;
        const { plan } = confirmChange;
        const wasElite = PLAN_RANKS[subscription?.plan] === 3;
        const willBeElite = plan.rank === 3;
        setChangeLoading(true);
        let crossedTier = false;
        try {
            const res = await changePlan(plan.priceId);
            setConfirmChange(null);
            setPickerOpen(false);
            if (res.changed === 'immediate') {
                showToast(`You're now on ${plan.label}`, 'success');
            } else {
                showToast(`Plan change scheduled for ${new Date(res.effectiveAt).toLocaleDateString()}`, 'success');
            }
            await loadSubscription();
            crossedTier = res.changed === 'immediate' && wasElite !== willBeElite;
        } catch (err) {
            setConfirmChange(null);
            showToast(err?.data?.error || 'Unable to change plan', 'error');
        } finally {
            setChangeLoading(false);
        }
        // An immediate change crossing the Premium↔Elite boundary swaps the Cognito group
        // (webhook, async). Refresh the token in the BACKGROUND so the new tier takes effect
        // without a sign-out — must not block the picker/changeLoading. billing.md §3.
        if (crossedTier) {
            waitForTier(willBeElite ? 2 : 1).then(setTier).catch(() => {});
        }
    };

    const handleCancelPendingChange = async () => {
        setChangeLoading(true);
        try {
            await cancelPlanChange();
            showToast('Keeping your current plan', 'success');
            await loadSubscription();
        } catch {
            showToast('Unable to cancel plan change', 'error');
        } finally {
            setChangeLoading(false);
        }
    };

    const handleConfirmCancelSubscription = async () => {
        setChangeLoading(true);
        try {
            const res = await cancelSubscription();
            setConfirmCancel(false);
            setPickerOpen(false);
            showToast(`Subscription will end on ${new Date(res.cancelAt).toLocaleDateString()}`, 'success');
            await loadSubscription();
        } catch (err) {
            setConfirmCancel(false);
            showToast(err?.data?.error || 'Unable to cancel subscription', 'error');
        } finally {
            setChangeLoading(false);
        }
    };

    const handleResumeSubscription = async () => {
        setChangeLoading(true);
        try {
            await resumeSubscription();
            showToast('Subscription resumed', 'success');
            await loadSubscription();
        } catch (err) {
            showToast(err?.data?.error || 'Unable to resume subscription', 'error');
        } finally {
            setChangeLoading(false);
        }
    };

    const handleFocusesChange = useCallback((newFocuses) => {
        setFocuses(newFocuses);
        const cached = getCachedProfile();
        if (cached) setCachedProfile({ ...cached, focuses: newFocuses });
    }, []);

    const handleProfileSave = async (data) => {
        setProfileSubmitting(true);
        try {
            const result = await saveUserProfile(data);
            const updated = result.profile || data;
            setCachedProfile(updated);
            setProfileData(updated);
            if (updated.sex) setSex(updated.sex);
            showToast('Profile updated', 'success');
        } catch (err) {
            console.error('Failed to save profile:', err);
            showToast('Failed to save profile', 'error');
        } finally {
            setProfileSubmitting(false);
        }
    };

    const handleNavClick = (id) => {
        setActiveSection(id);
        setMobileShowContent(true);
    };

    const handleMobileBack = () => {
        setMobileShowContent(false);
    };

    if (!authChecked) return null;

    if (isDoctor) {
        return (
            <div className="settings-container">
                <UserHeader doctorMode />
                <div className="settings-content--doctor">
                    <button className="settings-back" onClick={() => navigate('/doctor')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5" />
                            <path d="M12 19l-7-7 7-7" />
                        </svg>
                        Back to Dashboard
                    </button>
                    <LabConfigSettings />
                </div>
            </div>
        );
    }

    const activeSectionLabel = patientSections.find(s => s.id === activeSection)?.label || 'Settings';

    return (
        <div className="settings-container">
            <UserHeader />
            <div className={`settings-layout ${mobileShowContent ? 'mobile-drilled' : ''}`}>
                <nav className="settings-nav">
                    <button className="settings-back" onClick={() => navigate('/home')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5" />
                            <path d="M12 19l-7-7 7-7" />
                        </svg>
                        Back to Home
                    </button>
                    <h1 className="settings-title">Settings</h1>
                    <div className="settings-nav-items">
                        {patientSections.map(s => (
                            <button
                                key={s.id}
                                className={`settings-nav-item ${activeSection === s.id ? 'active' : ''}`}
                                onClick={() => handleNavClick(s.id)}
                            >
                                <span className="settings-nav-label">{s.label}</span>
                                <span className="settings-nav-desc">{s.desc}</span>
                                <svg className="settings-nav-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        ))}
                    </div>
                </nav>

                <div className="settings-content">
                    <button className="settings-content-back" onClick={handleMobileBack}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5" />
                            <path d="M12 19l-7-7 7-7" />
                        </svg>
                        {activeSectionLabel}
                    </button>

                    {activeSection === 'profile' && (
                        <section className="settings-section">
                            <h2 className="settings-section-title">Profile</h2>
                            <p className="settings-section-desc">Your personal information used for lab analysis accuracy.</p>
                            <div className="settings-section-body">
                                {profileLoading ? (
                                    <div className="settings-loading">Loading profile...</div>
                                ) : (
                                    <ProfileForm
                                        initialData={profileData}
                                        onSave={handleProfileSave}
                                        submitting={profileSubmitting}
                                    />
                                )}
                            </div>
                        </section>
                    )}

                    {activeSection === 'focuses' && (
                        <>
                            <FocusSettings initialFocuses={focuses} onFocusesChange={handleFocusesChange} isPremium={tier >= 1} />
                            <AssessmentSettings focuses={focuses} sex={sex} />
                        </>
                    )}

                    {activeSection === 'sharing' && (
                        <section className="settings-section">
                            <h2 className="settings-section-title">Sharing & Access</h2>
                            <p className="settings-section-desc">Manage who can view your lab results and health data.</p>
                            <div className="settings-section-body">
                                <div className="settings-sharing-actions">
                                    <button
                                        className="settings-action-card"
                                        onClick={() => navigate('/access')}
                                    >
                                        <div className="settings-action-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                <circle cx="9" cy="7" r="4" />
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                            </svg>
                                        </div>
                                        <div className="settings-action-text">
                                            <span className="settings-action-title">Manage Sharing</span>
                                            <span className="settings-action-subtitle">View and manage doctor and friend connections</span>
                                        </div>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                    <button
                                        className="settings-action-card"
                                        onClick={() => navigate('/peer')}
                                    >
                                        <div className="settings-action-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                                <circle cx="9" cy="7" r="4" />
                                                <line x1="19" y1="8" x2="19" y2="14" />
                                                <line x1="22" y1="11" x2="16" y2="11" />
                                            </svg>
                                        </div>
                                        <div className="settings-action-text">
                                            <span className="settings-action-title">Peer Dashboard</span>
                                            <span className="settings-action-subtitle">View shared results from friends</span>
                                        </div>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </section>
                    )}

                    {activeSection === 'notifications' && (
                        <NotificationSettings />
                    )}

                    {activeSection === 'advanced' && (
                        <LabConfigSettings />
                    )}

                    {activeSection === 'subscription' && (
                        <section className="settings-section">
                            <h2 className="settings-section-title">Subscription</h2>
                            <p className="settings-section-desc">Manage your plan and billing.</p>
                            <div className="settings-section-body">
                                {subLoading ? (
                                    <div className="settings-loading">Loading subscription...</div>
                                ) : subError ? (
                                    <div className="settings-sub-error">
                                        <p>Unable to load subscription.</p>
                                        <button className="settings-sub-retry" onClick={loadSubscription}>Retry</button>
                                    </div>
                                ) : subscription && (subscription.status === 'active' || subscription.status === 'trialing') ? (
                                    <div className="settings-sub-card">
                                        <div className="settings-sub-row">
                                            <span className="settings-sub-label">Status</span>
                                            <span className={`settings-sub-badge ${subscription.status === 'trialing' ? 'badge-trial' : 'badge-active'}`}>
                                                {subscription.status === 'trialing' ? 'Trial' : 'Active'}
                                            </span>
                                        </div>
                                        {subscription.plan && (
                                            <div className="settings-sub-row">
                                                <span className="settings-sub-label">Plan</span>
                                                <span className="settings-sub-value">{PLAN_NAMES[subscription.plan] || 'Plan'}</span>
                                            </div>
                                        )}
                                        {subscription.currentPeriodEnd && (
                                            <div className="settings-sub-row">
                                                <span className="settings-sub-label">
                                                    {subscription.cancelAtPeriodEnd ? 'Ends' : subscription.status === 'trialing' ? 'Trial ends' : 'Renews'}
                                                </span>
                                                <span className="settings-sub-value">
                                                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                                </span>
                                            </div>
                                        )}
                                        {subscription.pendingPlan && (() => {
                                            // Elite doesn't auto-renew — it rolls into Annual at term end.
                                            // Give that case its own copy + a "Keep Elite" affordance.
                                            const isEliteRollover = PLAN_RANKS[subscription.plan] === 3;
                                            const effectiveDate = subscription.pendingPlanEffective
                                                && new Date(subscription.pendingPlanEffective).toLocaleDateString();
                                            return (
                                                <div className="settings-sub-pending">
                                                    {isEliteRollover ? (
                                                        <span>
                                                            Your Elite membership converts to <strong>{PLAN_NAMES[subscription.pendingPlan] || 'Annual'}</strong>
                                                            {effectiveDate && <> on {effectiveDate}</>}. Elite does not auto-renew.
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            Your plan changes to <strong>{PLAN_NAMES[subscription.pendingPlan] || 'a new plan'}</strong>
                                                            {effectiveDate && <> on {effectiveDate}</>}.
                                                        </span>
                                                    )}
                                                    <button
                                                        className="settings-sub-pending-undo"
                                                        onClick={handleCancelPendingChange}
                                                        disabled={changeLoading}
                                                    >
                                                        {changeLoading ? 'Working...' : isEliteRollover ? 'Keep Elite' : 'Keep current plan'}
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                        {subscription.cancelAtPeriodEnd && (
                                            <div className="settings-sub-pending">
                                                <span>
                                                    Your subscription is set to cancel
                                                    {subscription.currentPeriodEnd && (
                                                        <> on <strong>{new Date(subscription.currentPeriodEnd).toLocaleDateString()}</strong></>
                                                    )}. You can resume or switch plans anytime before then.
                                                </span>
                                                <button
                                                    className="settings-sub-pending-undo"
                                                    onClick={handleResumeSubscription}
                                                    disabled={changeLoading}
                                                >
                                                    {changeLoading ? 'Working...' : 'Resume'}
                                                </button>
                                            </div>
                                        )}
                                        <div className="settings-sub-actions">
                                            <button
                                                className="settings-sub-btn settings-sub-btn-change"
                                                onClick={() => setPickerOpen(open => !open)}
                                                disabled={changeLoading}
                                            >
                                                {pickerOpen ? 'Close' : 'Change Plan'}
                                            </button>
                                            {!subscription.cancelAtPeriodEnd && (
                                                <button
                                                    className="settings-sub-btn settings-sub-btn-cancel"
                                                    onClick={() => setConfirmCancel(true)}
                                                    disabled={changeLoading}
                                                >
                                                    Cancel Subscription
                                                </button>
                                            )}
                                        </div>
                                        <div className="settings-sub-links">
                                            <button
                                                className="settings-sub-portal-link"
                                                onClick={() => navigate('/billing')}
                                            >
                                                Billing history
                                            </button>
                                            <button
                                                className="settings-sub-portal-link"
                                                onClick={handlePortal}
                                                disabled={portalLoading}
                                            >
                                                {portalLoading ? 'Opening...' : 'Manage payment methods'}
                                            </button>
                                        </div>
                                        {pickerOpen && (
                                            <div className="settings-plan-picker">
                                                {SWITCHABLE_PLANS
                                                    // Exclude the current plan — match by label too so legacy
                                                    // price IDs of the same plan don't slip through — and any
                                                    // already-pending target (the banner covers it).
                                                    .filter(p => p.priceId !== subscription.plan
                                                        && p.label !== PLAN_NAMES[subscription.plan]
                                                        && p.priceId !== subscription.pendingPlan)
                                                    .map(plan => {
                                                        const currentRank = PLAN_RANKS[subscription.plan] || 0;
                                                        const isUpgrade = plan.rank > currentRank;
                                                        return (
                                                            <div key={plan.priceId} className="settings-plan-option">
                                                                <div className="settings-plan-option-info">
                                                                    <span className="settings-plan-option-name">{plan.label}</span>
                                                                    <span className="settings-plan-option-price">{plan.amount}{plan.interval}</span>
                                                                    <span className="settings-plan-option-note">
                                                                        {isUpgrade
                                                                            ? "Switches today — you'll be charged the prorated difference now."
                                                                            : `Takes effect on ${subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'your renewal date'} — you keep your current plan until then.`}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    className="settings-sub-btn settings-plan-option-btn"
                                                                    onClick={() => handleSelectPlan(plan)}
                                                                    disabled={changeLoading}
                                                                >
                                                                    {loadingPlanId === plan.priceId ? 'Working...' : isUpgrade ? 'Upgrade' : 'Switch'}
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                        {confirmChange && (
                                            <div className="settings-confirm-overlay" onClick={() => !changeLoading && setConfirmChange(null)}>
                                                <div className="settings-confirm-modal" onClick={(e) => e.stopPropagation()}>
                                                    <h3>Switch to {confirmChange.plan.label}?</h3>
                                                    <div className="settings-confirm-row">
                                                        <span>Due today</span>
                                                        <strong>
                                                            {(confirmChange.preview.amountDueNow / 100).toLocaleString('en-US', {
                                                                style: 'currency',
                                                                currency: (confirmChange.preview.currency || 'usd').toUpperCase(),
                                                            })}
                                                        </strong>
                                                    </div>
                                                    <div className="settings-confirm-row">
                                                        <span>Current period ends</span>
                                                        <strong>{new Date(confirmChange.preview.currentPeriodEnd).toLocaleDateString()}</strong>
                                                    </div>
                                                    <p className="settings-confirm-note">
                                                        {confirmChange.preview.changeType === 'immediate'
                                                            ? `${confirmChange.plan.label} (${confirmChange.plan.amount}${confirmChange.plan.interval}) starts today and replaces the remainder of your current period. Any residual balance is credited to the amount due.`
                                                            : `No charge today. You keep your current plan until ${new Date(confirmChange.preview.effectiveAt).toLocaleDateString()}, then ${confirmChange.plan.label} (${confirmChange.plan.amount}${confirmChange.plan.interval}) begins.`}
                                                        {confirmChange.preview.resumes && ' This also resumes your subscription — the scheduled cancellation is removed.'}
                                                    </p>
                                                    <div className="settings-confirm-actions">
                                                        <button
                                                            className="settings-sub-btn settings-confirm-cancel"
                                                            onClick={() => setConfirmChange(null)}
                                                            disabled={changeLoading}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            className="settings-sub-btn settings-confirm-confirm"
                                                            onClick={handleConfirmChange}
                                                            disabled={changeLoading}
                                                        >
                                                            {changeLoading ? 'Working...' : 'Confirm'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {confirmCancel && (
                                            <div className="settings-confirm-overlay" onClick={() => !changeLoading && setConfirmCancel(false)}>
                                                <div className="settings-confirm-modal" onClick={(e) => e.stopPropagation()}>
                                                    <h3>Cancel subscription?</h3>
                                                    <div className="settings-confirm-row">
                                                        <span>Due today</span>
                                                        <strong>$0.00</strong>
                                                    </div>
                                                    {subscription.currentPeriodEnd && (
                                                        <div className="settings-confirm-row">
                                                            <span>Access until</span>
                                                            <strong>{new Date(subscription.currentPeriodEnd).toLocaleDateString()}</strong>
                                                        </div>
                                                    )}
                                                    <p className="settings-confirm-note">
                                                        You keep full access until the end of your current period, and you
                                                        won't be charged again. You can resume or switch plans anytime before then.
                                                    </p>
                                                    {PLAN_RANKS[subscription.plan] === 3 && (
                                                        <p className="settings-confirm-note settings-confirm-elite-note">
                                                            As an Elite member, if you wish to end your services immediately,
                                                            you may be eligible for a prorated refund. Please contact support
                                                            for assistance. Otherwise, your plan will continue until it's
                                                            expiration date.
                                                        </p>
                                                    )}
                                                    <div className="settings-confirm-actions">
                                                        <button
                                                            className="settings-sub-btn settings-confirm-cancel"
                                                            onClick={() => setConfirmCancel(false)}
                                                            disabled={changeLoading}
                                                        >
                                                            Keep Subscription
                                                        </button>
                                                        <button
                                                            className="settings-sub-btn settings-confirm-confirm"
                                                            onClick={handleConfirmCancelSubscription}
                                                            disabled={changeLoading}
                                                        >
                                                            {changeLoading ? 'Working...' : 'Confirm Cancellation'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : subscription && subscription.status === 'canceled' ? (
                                    <div className="settings-sub-card settings-sub-empty">
                                        <p>Your subscription has ended.</p>
                                        <button className="settings-sub-btn settings-sub-btn-change" onClick={() => navigate('/pricing')}>
                                            Resubscribe
                                        </button>
                                    </div>
                                ) : (
                                    <div className="settings-sub-card settings-sub-empty">
                                        <p>No active subscription.</p>
                                        <button className="settings-sub-btn settings-sub-btn-change" onClick={() => navigate('/pricing')}>
                                            View Plans
                                        </button>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Settings;
